import { useEffect, useRef, useState } from 'react';
import { generateImageFileNameWithRandomName } from '../../../utils/generateImageFileName';
import moment from 'moment';
import { IMAGE_POPUP_ROOT_URL } from '../../../constants/imageUrl';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { BannerModel } from '../../../models/banner';
import { BannerEnum, BannerSlideEnum } from '../../../enums/bannerEnum';
import { SelectChangeEvent } from '@mui/material';
import { getMainGroupByMerchantId } from '../../../services/mainGroup';
import { MainGroupModel } from '../../../models/mainGroup';
import { DivisionModel } from '../../../models/division';
import { CategoriesModel } from '../../../models/categories';
import { SubCategoryModel } from '../../../models/subCategory';
import { ProductMerchantModel } from '../../../models/productMerchant';
import { handleGetProductByItemCode } from '../../../utils/getProductByItemCodeFunction';
import {
  createBannerCouponService,
  createBannerPromotionService,
  createBannerSlideService,
  updateBannerCouponService,
  updateBannerPromotionService,
  updateBannerSlideService,
} from '../../../services/banner';
import { getProductMerchantById } from '../../../services/productMerchant';
import { useTranslation } from 'react-i18next';

interface Props {
  selectedData: BannerModel;
  onGetData: any;
  onClose: VoidFunction;
  bannerType: BannerEnum;
}

interface CategoryMatch {
  mainGroup: MainGroupModel | null;
  division: DivisionModel | null;
  category: CategoriesModel | null;
  subCategory: any | null;
}

export const useBannerFormManageControllers = ({
  selectedData,
  onGetData,
  onClose,
  bannerType,
}: Props) => {
  const { t } = useTranslation('global');
  // variables
  const getUsername = useSelector((state: RootState) => state?.auth?.data?.username);

  const [imageFile, setImageFile] = useState<any>(null);
  const [imageToShow, setImageToShow] = useState<any>(null);

  const startDateRef = useRef<HTMLInputElement>(null!);
  const endDateRef = useRef<HTMLInputElement>(null!);
  const itemCodeRef = useRef<HTMLInputElement>(null!);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);

  const [selectedTypeBanner, setSelectedTypeBanner] = useState<BannerSlideEnum>(
    BannerSlideEnum.CATEGORY
  );
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>('');
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [selectTagPromotion, setSelectTagPromotion] = useState<string>('');
  const [selectedMerchantValue, setSelectedMerchantValue] = useState<string>('');

  const [mainGroupData, setMainGroupData] = useState<MainGroupModel[]>([]);
  const [divisionData, setDivisionData] = useState<DivisionModel[]>([]);
  const [categoriesData, setCategoriesData] = useState<CategoriesModel[]>([]);
  const [subCategoryData, setSubCategoryData] = useState<SubCategoryModel[]>([]);

  const [productData, setProductData] = useState<ProductMerchantModel[]>([]);
  const [productValue, setProductValue] = useState<string>(null!);
  
  // functions

  const handleGetProduct = async (): Promise<void> => {
    handleGetProductByItemCode(itemCodeRef.current.value, setProductData, setErrorMessage);
  };

  const handleSelectedBannerType = (event: SelectChangeEvent<BannerSlideEnum>) => {
    setSelectedTypeBanner(event.target.value as BannerSlideEnum);
  };

  const handleSelectedMerchant = (event: SelectChangeEvent<string>) => {
    setSelectedMerchantValue(event.target.value as string);
  };

  const handleSelectMainGroup = (event: SelectChangeEvent<string>) => {
    setSelectedMainCategory(event.target.value as string);
  };

  const handleSelectDivision = (event: SelectChangeEvent<string>) => {
    setSelectedDivision(event.target.value as string);
  };

  const handleSelectCategories = (event: SelectChangeEvent<string>) => {
    setSelectedCategories(event.target.value as string);
  };

  const handleSelectSubCategory = (event: SelectChangeEvent<string>) => {
    setSelectedSubCategory(event.target.value as string);
  };

  const handleSelectProduct = (event: SelectChangeEvent<string>) => {
    setProductValue(event.target.value);
  };

  const handleSelectTagPromotion = (event: SelectChangeEvent<string>) => {
    setSelectTagPromotion(event.target.value);
  };

  const handleSelectImage = (e: any) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    setImageFile(file);
    reader.onload = () => {
      if (reader.readyState === 2) {
        setImageToShow(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setImageFile(file);
  };

  const handleFormValidate = (): boolean => {
    const getStartDate = new Date(startDateRef.current.value);
    const getEndDate = new Date(endDateRef.current.value);
    const today = new Date().setHours(0, 0, 0, 0);

    if (getStartDate >= getEndDate) {
      setErrorMessage(t('popup_page.Start date must be less than End date') || '');
      return false;
    }
    if (getEndDate.setHours(0, 0, 0, 0) < today) {
      setErrorMessage(t('popup_page.End date must be greater or equal to today') || '');
      return false;
    }
    if (!selectedData && !imageFile) {
      setErrorMessage(t('popup_page.Please select image') || '');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleConfirmDialog = () => {
    if (selectedData) {
      handleUpdateData();
    } else {
      handleAddData();
    }
  };

  const handleGetCategories = async () => {
    try {
      setLoadingCategories(true);
      const resCat = await getMainGroupByMerchantId({ merchantId: selectedMerchantValue });
      setMainGroupData(resCat);
    } catch (err: any) {
      try {
        window.alert(err.response.data.message);
      } catch (err2) {
        window.alert(t('popup_page.Server error please try again later'));
      }
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleGetProductMerchant = async () => {
    try {
      const res = await getProductMerchantById(selectedData?.productId, selectedMerchantValue);
      setProductData(res);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCreateBannerSlide = async () => {
    const currentDate = moment(); 
    const startDate = moment(new Date(startDateRef.current.value));
    const endDate = moment(new Date(endDateRef.current.value)).subtract(1, 'days');
    const isActive = currentDate.isSameOrAfter(startDate);

    const input = {
      merchant: selectedMerchantValue,
      productId: productValue,
      promotionTagId: selectTagPromotion,
      subCategoryId: selectedSubCategory,
      isActive: isActive,
      image: generateImageFileNameWithRandomName(imageFile),
      startDate: startDate.format('MM/DD/yyyy'),
      endDate: endDate.format('MM/DD/yyyy'),
      type: bannerType,
    };
    await createBannerSlideService(input);
  };

  const handleCreateCoupon = async () => {
    const currentDate = moment(); 
    const startDate = moment(new Date(startDateRef.current.value));
    const endDate = moment(new Date(endDateRef.current.value)).subtract(1, 'days');
    const isActive = currentDate.isSameOrAfter(startDate);

    const input = {
      merchant: selectedMerchantValue,
      isActive: isActive,
      type: bannerType,
      image: generateImageFileNameWithRandomName(imageFile),
      startDate: startDate.format('MM/DD/yyyy'),
      endDate: endDate.format('MM/DD/yyyy'),
    };
    await createBannerCouponService(input);
  };

  const handleCreatePromotion = async () => {
    const currentDate = moment(); 
    const startDate = moment(new Date(startDateRef.current.value));
    const endDate = moment(new Date(endDateRef.current.value)).subtract(1, 'days');
    const isActive = currentDate.isSameOrAfter(startDate);

    const input = {
      merchant: selectedMerchantValue,
      promotionTagId: selectTagPromotion,
      isActive: isActive,
      type: bannerType,
      image: generateImageFileNameWithRandomName(imageFile),
      startDate: startDate.format('MM/DD/yyyy'),
      endDate: endDate.format('MM/DD/yyyy'),
      updatedBy: getUsername,
    };
    await createBannerPromotionService(input);
  };

  const handleAddData = async () => {
    try {
      setLoadingSubmit(true);
      if (bannerType === BannerEnum.COUPON) {
        await handleCreateCoupon();
      } else if (bannerType === BannerEnum.PROMOTION) {
        await handleCreatePromotion();
      } else {
        await handleCreateBannerSlide();
      }
      await onGetData();
      onClose();
    } catch (err: any) {
      setErrorMessage(
        err.response.data.message ?? t('popup_page.Server error please try again later')
      );
    } finally {
      setLoadingSubmit(false);
      setOpenConfirmDialog(false);
    }
  };

  const handleUpdateBannerSlide = async () => {
    const input: any = {
      startDate: moment(new Date(startDateRef.current.value)).format('MM/DD/yyyy'),
      endDate: moment(new Date(endDateRef.current.value)).subtract(1, 'days').format('MM/DD/yyyy'),
    };
    if (imageFile) {
      input.image = generateImageFileNameWithRandomName(imageFile);
    }
    await updateBannerSlideService(selectedData?.id, input);
  };

  const handleUpdateBannerPromotion = async () => {
    const input: any = {
      startDate: moment(new Date(startDateRef.current.value)).format('MM/DD/yyyy'),
      endDate: moment(new Date(endDateRef.current.value)).subtract(1, 'days').format('MM/DD/yyyy'),
      updatedBy: getUsername,
    };
    if (imageFile) {
      input.image = generateImageFileNameWithRandomName(imageFile);
    }
    await updateBannerPromotionService(selectedData?.id, input);
  };

  const handleUpdateBannerCoupon = async () => {
    const input: any = {
      startDate: moment(new Date(startDateRef.current.value)).format('MM/DD/yyyy'),
      endDate: moment(new Date(endDateRef.current.value)).subtract(1, 'days').format('MM/DD/yyyy'),
      updatedBy: getUsername,
    };
    if (imageFile) {
      input.image = generateImageFileNameWithRandomName(imageFile);
    }
    await updateBannerCouponService(selectedData?.id, input);
  };

  const handleUpdateData = async () => {
    try {
      setLoadingSubmit(true);
      if (bannerType === BannerEnum.COUPON) {
        await handleUpdateBannerCoupon();
      } else if (bannerType === BannerEnum.PROMOTION) {
        await handleUpdateBannerPromotion();
      } else {
        await handleUpdateBannerSlide();
      }
      await onGetData();
      onClose();
    } catch (err: any) {
      setErrorMessage(
        err.response.data.message ?? t('popup_page.Server error please try again later')
      );
    } finally {
      setLoadingSubmit(false);
      setOpenConfirmDialog(false);
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (handleFormValidate()) {
      setOpenConfirmDialog(true);
    }
  };

  const findCategoryDataBySubCategoryId = (
    mainGroupData: MainGroupModel[],
    targetSubCategoryId: string
  ): CategoryMatch => {
    let result: CategoryMatch = {
      mainGroup: null,
      division: null,
      category: null,
      subCategory: null,
    };

    // Traverse through main groups
    for (const mainGroup of mainGroupData) {
      if (!mainGroup.division_name) continue;

      // Traverse through divisions
      for (const division of mainGroup.division_name) {
        if (!division.product_category) continue;

        // Traverse through categories
        for (const category of division.product_category) {
          if (!category.product_sub_category) continue;

          // Find matching subcategory
          const foundSubCategory = category.product_sub_category.find(
            (sub) => sub.id === targetSubCategoryId
          );

          if (foundSubCategory) {
            result = {
              mainGroup: mainGroup,
              division: division,
              category: category,
              subCategory: foundSubCategory,
            };
            // Return immediately when match is found
            return result;
          }
        }
      }
    }
    return result;
  };

  const convertDateStringToNormalString = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Format to YYYY-MM-DD as required by the date input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getBannerType = (item: BannerModel) =>
    item?.productId
      ? BannerSlideEnum.PRODUCT_DETAIL
      : item?.promotionTagId
      ? BannerSlideEnum.PROMOTION_FLASH_SALE
      : item?.subCategoryId
      ? BannerSlideEnum.CATEGORY
      : BannerSlideEnum.COUPON;

  // useEffect
  useEffect(() => {
    if (selectedData) {
      startDateRef.current.value = convertDateStringToNormalString(selectedData?.startDate);
      endDateRef.current.value = convertDateStringToNormalString(selectedData?.endDate);

      setSelectedTypeBanner((prevState) => {
        if (selectedData?.promotionTagId) {
          return BannerSlideEnum.PROMOTION_FLASH_SALE;
        }
        if (selectedData?.subCategoryId) {
          return BannerSlideEnum.CATEGORY;
        }
        return prevState;
      });

      setImageToShow(IMAGE_POPUP_ROOT_URL + selectedData?.image?.url);
      setSelectedMerchantValue(selectedData?.merchant);
      setSelectedSubCategory(selectedData?.subCategoryId);
      setSelectTagPromotion(selectedData?.promotionTagId);
      setSelectedTypeBanner(getBannerType(selectedData));

      if (selectedData.subCategoryId && mainGroupData) {
        const foundData = findCategoryDataBySubCategoryId(
          mainGroupData,
          selectedData.subCategoryId
        );
        // Add null checks before accessing properties
        if (foundData) {
          if (foundData.mainGroup?.id) {
            setSelectedMainCategory(foundData.mainGroup.id);
          }
          if (foundData.division?.id) {
            setSelectedDivision(foundData.division.id);
          }
          if (foundData.category?.id) {
            setSelectedCategories(foundData.category.id);
          }
          if (foundData.subCategory?.id) {
            setSelectedSubCategory(foundData.subCategory.id);
          }
        } else {
          console.warn(
            'No matching category data found for subCategoryId:',
            selectedData.subCategoryId
          );
        }
      }
      if (selectedData.productId) {
        const foundProduct = productData?.find((item) => item?.id === selectedData?.productId);
        if (foundProduct) {
          setProductValue(foundProduct?.id);
          itemCodeRef.current.value = foundProduct?.price?.itemcode;
        } else {
          console.warn('Product not found for the given productId:', selectedData.productId);
          setProductValue('');
        }
      }
    }
  }, [selectedData, mainGroupData, productData]);

  useEffect(() => {
    handleGetCategories();
    if (selectedData?.productId) {
      handleGetProductMerchant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMerchantValue]);

  useEffect(() => {
    if (!selectedMainCategory) return;
    const mainData = mainGroupData?.find(
      (item: MainGroupModel) => item?.id === selectedMainCategory
    );
    const divisionData = mainData?.division_name;
    setSubCategoryData([]);
    setCategoriesData([]);
    setDivisionData([]);

    if (divisionData) {
      setSubCategoryData([]);
      setCategoriesData([]);
      setDivisionData(divisionData);
      if (!selectedDivision) return;
      const categoriesData = divisionData?.find(
        (item: DivisionModel) => item?.id === selectedDivision
      )?.product_category;
      if (categoriesData) {
        setSubCategoryData([]);
        setCategoriesData(categoriesData);
        if (!selectedCategories) return;
        const selectedCategory = categoriesData?.find(
          (item: CategoriesModel) => item?.id === selectedCategories
        );
        const getSubCategoryData = selectedCategory?.product_sub_category;
        if (getSubCategoryData) {
          setSubCategoryData(getSubCategoryData);
        }
      }
    }
  }, [selectedMainCategory, mainGroupData, selectedDivision, selectedCategories]);

  return {
    startDateRef,
    endDateRef,
    imageToShow,
    errorMessage,
    loadingSubmit,
    openConfirmDialog,
    divisionData,
    categoriesData,
    itemCodeRef,
    productData,
    productValue,
    subCategoryData,
    mainGroupData,
    loadingCategories,
    selectedData,
    selectedTypeBanner,
    selectedCategories,
    selectedDivision,
    selectedSubCategory,
    selectTagPromotion,
    selectedMainCategory,
    selectedMerchantValue,
    handleSelectImage,
    handleSubmit,
    handleConfirmDialog,
    handleSelectedBannerType,
    handleSelectedMerchant,
    handleSelectMainGroup,
    handleSelectDivision,
    handleSelectCategories,
    handleSelectSubCategory,
    handleSelectProduct,
    handleGetProduct,
    handleSelectTagPromotion,
    handleSetOpenConfirmDialog: (newValue: boolean) => setOpenConfirmDialog(newValue),
  };
};
